if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.querySelectorAll('.media-gallery__arrow').forEach((arrow) => {
          arrow.addEventListener('click', this.onArrowClick.bind(this));
        });
      }

      onArrowClick(event) {
        const direction = event.currentTarget.classList.contains('media-gallery__arrow--next') ? 1 : -1;
        const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        if (items.length < 2) return;
        const current = this.elements.thumbnails.querySelector('button[aria-current="true"]');
        const currentIndex = current ? items.indexOf(current.closest('[data-target]')) : 0;
        const nextIndex = (currentIndex + direction + items.length) % items.length;
        items[nextIndex].querySelector('button').click();
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        // The slider derives currentElement from measurements taken on load;
        // if those are stale (images still settling) the index can point past
        // the last slide. Bail instead of throwing and losing the thumbnail
        // sync for the rest of the session.
        if (!activeMedia) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        // A swiped-to slide has to be marked active like a clicked thumbnail
        // is: on a video slide the <deferred-media> is display:none until its
        // slide carries .is-active, so without this the video played behind
        // the still preview image and the shopper had to tap the play icon.
        this.markActiveSlide(activeMedia);
        this.playActiveMedia(activeMedia);
      }

      markActiveSlide(activeMedia) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
        // Force a layout flush before anything tries to play this slide's
        // video. Safari decides whether autoplay is allowed from the element's
        // visibility at the moment play() is called, and playActiveMedia()
        // runs in this same synchronous block — without this read the class
        // above is not applied yet and the player still computes as
        // display:none, so Safari silently declines to start it.
        void activeMedia.offsetHeight;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.markActiveSlide(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);

        // Resume autoplay/loop videos when their slide becomes active again.
        // pauseAllMedia() pauses everything on slide change, but autoplay only
        // fires on first load, so a local <video> stays paused on return.
        const activeVideo = activeItem.querySelector('video');
        if (activeVideo && (activeVideo.hasAttribute('loop') || activeVideo.hasAttribute('autoplay'))) {
          activeVideo.muted = true;
          activeVideo.play().catch(() => {});
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/*
 * Autoplay for Shopify-hosted product videos in the media gallery.
 *
 * Dawn only starts a gallery video as a side effect of the slider's
 * `slideChanged` event. That path needs a thumbnail rail to be wired up at all
 * (see the constructor's early return) and correct slide measurements to
 * resolve the right slide, so on mobile a swipe frequently left the video
 * sitting behind its play poster waiting for a tap.
 *
 * Driving playback off "is this slide actually on screen?" instead removes
 * both dependencies, and applies to every product video in the store —
 * including ones added later — with no per-product setup.
 */
(() => {
  const GALLERY_VIDEO = 'media-gallery [id^="GalleryViewer"] deferred-media';
  const PLAY_RATIO = 0.25;
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAY = 300;

  if (!('IntersectionObserver' in window) || window.productVideoAutoplay) return;
  window.productVideoAutoplay = true;

  const onScreen = new Set();

  // External video (YouTube/Vimeo) is an iframe that already gets autoplay=1
  // in its URL and can't be driven from here.
  function hasNativeVideo(deferredMedia) {
    if (deferredMedia.querySelector('video')) return true;
    const template = deferredMedia.querySelector('template');
    return Boolean(template && template.content.querySelector('video'));
  }

  function startPlayback(video, attempt = 1) {
    // iOS only plays unattended when the video is muted and inline. Both are
    // already attributes on the tag; set the properties too, since the element
    // is cloned out of a <template>.
    video.muted = true;
    video.playsInline = true;

    const retry = () => {
      if (attempt < MAX_ATTEMPTS) startPlayback(video, attempt + 1);
    };

    video.play().then(
      () => {
        const deferredMedia = video.closest('deferred-media');
        if (deferredMedia) deferredMedia.classList.remove('deferred-media--play-blocked');
        // Safari can resolve play() and stop the video anyway — typically when
        // it judged visibility before layout settled, or when Dawn's
        // pauseAllMedia() lands right behind us. A resolved promise is not
        // proof of playback, so check that the clock actually moved.
        const startedAt = video.currentTime;
        setTimeout(() => {
          if (video.ended) return;
          if (video.paused || video.currentTime === startedAt) retry();
        }, RETRY_DELAY);
      },
      (error) => {
        if (error && error.name === 'NotAllowedError') {
          // Refused outright (iOS Low Power Mode, data saver, …). Give the
          // play poster back so there is still a way to start it by hand.
          const deferredMedia = video.closest('deferred-media');
          if (deferredMedia) deferredMedia.classList.add('deferred-media--play-blocked');
          return;
        }
        // Usually the play being interrupted while the video was still
        // fetching. Retry on a timer rather than on `canplay` alone: an
        // already-buffered video never fires canplay again, so waiting for it
        // would strand the retry forever.
        setTimeout(retry, RETRY_DELAY);
      }
    );
  }

  function activate(deferredMedia) {
    if (!deferredMedia.hasAttribute('loaded')) {
      if (typeof deferredMedia.loadContent !== 'function') return;
      deferredMedia.classList.remove('deferred-media--play-blocked');
      deferredMedia.loadContent(false);
    }
    const video = deferredMedia.querySelector('video');
    if (video) startPlayback(video);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const deferredMedia = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO) {
          onScreen.add(deferredMedia);
          activate(deferredMedia);
        } else if (!entry.isIntersecting) {
          // Only stop once the slide is fully gone. Pausing on partial
          // visibility would fight playActiveMedia(): the player is roughly a
          // third of a phone screen, so a shopper who has scrolled a little
          // can drop below the play threshold with the video still in view.
          onScreen.delete(deferredMedia);
          const video = deferredMedia.querySelector('video');
          if (video) video.pause();
        }
      });
    },
    { threshold: [0, PLAY_RATIO] }
  );

  function scan() {
    document.querySelectorAll(GALLERY_VIDEO).forEach((deferredMedia) => {
      if (deferredMedia.dataset.autoplayObserved || !hasNativeVideo(deferredMedia)) return;
      deferredMedia.dataset.autoplayObserved = 'true';
      observer.observe(deferredMedia);
    });
  }

  function init() {
    scan();

    // Picking a variant re-renders the product section, so the gallery we are
    // observing gets replaced by fresh markup.
    let pending;
    new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(scan, 100);
    }).observe(document.body, { childList: true, subtree: true });

    // Browsers pause media in a backgrounded tab and don't resume it.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      onScreen.forEach(activate);
    });

    // Last resort for iOS: Dawn debounces slideChanged by 500ms, so the play()
    // after a swipe lands long outside the gesture window and Safari may
    // decline it. A play() driven by a touch always carries permission, so
    // pick up any on-screen video that is still sitting there paused.
    document.addEventListener(
      'touchend',
      () => {
        onScreen.forEach((deferredMedia) => {
          const video = deferredMedia.querySelector('video');
          if (video && video.paused && !video.ended) startPlayback(video);
        });
      },
      { passive: true }
    );
  }

  customElements.whenDefined('deferred-media').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  });
})();
